import { CalendarOptions, DateSelectArg, EventClickArg, EventMountArg } from '@fullcalendar/core'
import { getEventClickTarget, isBadgelessEventType } from './CalendarView.helpers'
import { calculateTableUtilizationForTime, orderResources } from './util'
import deLocale from '@fullcalendar/core/locales/de'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import luxonPlugin from '@fullcalendar/luxon3'
import FullCalendar from '@fullcalendar/react'
import { ResourceLabelContentArg } from '@fullcalendar/resource'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import scrollGridPlugin from '@fullcalendar/scrollgrid'
import { CalendarType, TenantType } from '@xeetr/types/enums'
import { CalendarEvent, FoodBookingModel, getEntityType } from '@xeetr/types/models'
import { getBooking } from '@xeetr/types/utils'
import { formatInTimeZone } from 'date-fns-tz'
import { RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import React from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { useUserSettings } from '@/hooks/useUserSettings'

import Loading from '../loading/Loading'

import { useCalendar, useTenant } from '@/hooks'
import { useMoveBooking } from '@/hooks/calendar/useMoveBooking'
import { useResourceReorder } from '@/hooks/calendar/useResourceReorder'

import {
  DndContext,
  DragOverlay,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { useCalendarUI } from '@/hooks/useCalendarUI'
import { useEvents } from '@/hooks/useEvents'
import { useSidebar } from '@/hooks/useSidebar'
import { SidebarType } from '@/types/Sidebar'
import { cn } from '@/lib/utils'
import { GripVertical, Repeat, UsersIcon } from 'lucide-react'
import { TableIcon } from '@/icons/TableIcon'
import { formatSlotDuration } from '@/utils/calendar'

interface Props {
  calendarRef: RefObject<FullCalendar | null>
  changeDate: (date: Date | Date[] | string) => void
}

const CalendarView = ({ changeDate, calendarRef }: Props) => {
  const {
    events,
    resources,
    eventsLoading,
    resourcesLoading,
    date: selectedDate,
    calendarType,
    setCalendarType,
  } = useCalendar()

  const { fullscreen } = useCalendarUI()
  const { setSidebar } = useSidebar()

  const { tenant, isLoading: tenantLoading } = useTenant()
  const { getCalendarPreferences, setCalendarPreferences } = useUserSettings()

  const prefs = getCalendarPreferences()
  const labelInterval = prefs?.timeInterval ?? 30
  const slotDurationPrefs = prefs?.slotDuration ?? 15
  const showUtilization = prefs?.showUtilization ?? false
  const initialWidth = prefs?.resourceAreaWidth ?? 200
  const { events: foodEvents } = useEvents()
  const [resourceAreaWidth, setResourceAreaWidth] = useState<number>(initialWidth)
  const [dividerPosition, setDividerPosition] = useState<number | null>(null)

  // call this to change an option on the running FullCalendar instance. The
  // earlier implementation rebuilt the whole calendar (via calendarKey) when
  // the width or other options changed; setOption is much cheaper.
  const updateCalendarOption = <OptionName extends keyof CalendarOptions>(
    name: OptionName,
    value: CalendarOptions[OptionName]
  ) => {
    if (calendarRef.current) {
      calendarRef.current.getApi().setOption(name, value)
    }
  }

  // when the user drags the resource divider we update the width state.
  // the same effect also measures the current width once (e.g. after
  // resources load). listeners are attached to document with delegation so the
  // calendar can swap DOM nodes without breaking the handlers. widthRef holds
  // the current width so the handler doesn't need to reattach on every change.
  const widthRef = useRef(resourceAreaWidth)
  useEffect(() => {
    widthRef.current = resourceAreaWidth
  }, [resourceAreaWidth])

  useEffect(() => {
    const measure = () => {
      let w: number | null = null
      const col = document.querySelector(
        '.fc-resource-timeline .fc-scrollgrid-sync-table col'
      ) as HTMLElement | null
      if (col) {
        w = Math.round(col.getBoundingClientRect().width)
      }
      const header = document.querySelector(
        'th.fc-col-header-cell.fc-resource'
      ) as HTMLElement | null
      if (header) {
        w = Math.round(header.getBoundingClientRect().width)
      }
      if (w && w !== resourceAreaWidth) {
        setResourceAreaWidth(w)
      }
      const div = document.querySelector('.fc-resource-timeline-divider') as HTMLElement | null
      if (div) {
        setDividerPosition(Math.round(div.getBoundingClientRect().left))
      }
    }

    measure()

    let isDragging = false
    let startX = 0
    let startWidth = 0

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return
      const delta = e.clientX - startX
      const newWidth = Math.max(50, Math.round(startWidth + delta))
      setResourceAreaWidth(newWidth)

      const div = document.querySelector('.fc-resource-timeline-divider') as HTMLElement | null
      if (div) {
        setDividerPosition(Math.round(div.getBoundingClientRect().left))
      }
    }

    const onMouseUp = () => {
      if (isDragging) {
        isDragging = false
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
    }

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target?.closest('.fc-resource-timeline-divider')) return
      isDragging = true
      startX = e.clientX
      startWidth = widthRef.current
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousedown', onMouseDown)

    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [resources, calendarType])

  useEffect(() => {
    if (prefs?.resourceAreaWidth && prefs.resourceAreaWidth !== resourceAreaWidth) {
      setResourceAreaWidth(prefs.resourceAreaWidth)
    }
  }, [prefs?.resourceAreaWidth])

  // persist preference whenever the measured width diverges from the
  // stored value. we only ignore the very first render when there is no prior
  // preference and the width still equals the default (initialWidth).
  useEffect(() => {
    if (resourceAreaWidth !== prefs?.resourceAreaWidth) {
      // skip saving if no preference yet and width unchanged from initial default
      if (prefs?.resourceAreaWidth === undefined && resourceAreaWidth === initialWidth) {
        return
      }

      const timer = setTimeout(() => {
        setCalendarPreferences({ resourceAreaWidth }, { silent: true })
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [resourceAreaWidth, prefs?.resourceAreaWidth, setCalendarPreferences, initialWidth])

  // make sure the calendar always reflects the current measured width; updates
  // are cheap thanks to setOption.
  useEffect(() => {
    updateCalendarOption('resourceAreaWidth', resourceAreaWidth)
  }, [resourceAreaWidth])

  // any other options that previously required a full reinitialization can be
  // updated in the same manner; for example showUtilization affects
  // slotLabelContent, which is passed as a prop, so React already handles it.

  const { moveBooking } = useMoveBooking()

  const calendarViewTypes = useMemo(() => {
    const hasResources = resources && resources.length > 0
    return {
      day:
        tenant?.type === TenantType.FOOD
          ? 'resourceTimeline'
          : hasResources
            ? 'resourceTimeGridDay'
            : 'dayGridDay',
      week: hasResources ? 'resourceTimeGridWeek' : 'dayGridWeek',
      month: 'dayGridMonth',
    }
  }, [tenant?.type, resources])

  const initialViewCalendar: string = useMemo(() => {
    switch (calendarType) {
      case CalendarType.DAY:
        return calendarViewTypes.day
      case CalendarType.WEEK:
        return calendarViewTypes.week
      case CalendarType.MONTH:
        return calendarViewTypes.month
      default:
        return calendarType
    }
  }, [calendarType, calendarViewTypes])

  const plugins = useMemo(
    () => [
      resourceTimeGridPlugin,
      resourceTimelinePlugin,
      interactionPlugin,
      scrollGridPlugin,
      luxonPlugin,
      dayGridPlugin,
    ],
    []
  )

  const orderedResources = useMemo(() => {
    return orderResources(resources || [], tenant?.calendarRoomOrder)
  }, [resources, JSON.stringify(tenant?.calendarRoomOrder)])

  // Resource reordering hooks
  const { moveResourceUp, moveResourceDown, reorderResources } =
    useResourceReorder(orderedResources)

  // track currently dragged resource for overlay
  const [activeId, setActiveId] = useState<string | null>(null)

  // sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const previousViewRef = useRef<string>('')

  // compute room-only ids for reordering
  const roomsOnly = useMemo(() => orderedResources.filter((r) => !r.parentId), [orderedResources])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const ids = roomsOnly.map((r) => r.id)
      const oldIndex = ids.indexOf(active.id as string)
      const newIndex = ids.indexOf(over.id as string)
      if (oldIndex === -1 || newIndex === -1) return

      const newOrder = arrayMove(ids, oldIndex, newIndex)
      reorderResources(newOrder)
    }
    setActiveId(null)
  }

  const selectDateSlot = (argument: DateSelectArg) => {
    switch (calendarType) {
      case CalendarType.DAY:
      case CalendarType.WEEK:
        const duration = argument.end.getTime() - argument.start.getTime()
        let end = argument.end
        if (duration <= slotDurationPrefs * 60 * 1000 && tenant?.defaultLengthOfStay) {
          const [hours, minutes] = tenant.defaultLengthOfStay.split(':').map(Number)
          const defaultDurationMinutes = hours * 60 + minutes
          end = new Date(argument.start.getTime() + defaultDurationMinutes * 60 * 1000)
        }

        setSidebar({
          open: true,
          type: SidebarType.BOOKING_CREATE,
          options: {
            start: argument.start,
            end: end,
            resourceId: argument.resource?.id,
            parentResourceId: argument.resource?.getParent()?.id,
          },
        })
        break
      case CalendarType.MONTH:
        setCalendarType(CalendarType.DAY)
        changeDate(argument.start)
        break
      default:
        throw new Error('Calendertype unknown')
    }
  }

  const handleOnEventClick = (argument: EventClickArg) => {
    switch (calendarType) {
      case CalendarType.DAY:
      case CalendarType.WEEK:
      case CalendarType.MONTH:
        const data = events?.find(
          (event) => event.id === argument.event._def.publicId
        ) as CalendarEvent
        const target = getEventClickTarget(data, calendarType === CalendarType.MONTH)
        switch (target.kind) {
          case 'absence':
            setSidebar({ open: true, type: SidebarType.ABSENCE_EDIT, absence: target.absence })
            break
          case 'monthBookingDrilldown':
            // Month-view booking cells are aggregates (e.g. "3 Reservierungen") whose event
            // data only carries ONE representative booking - opening it directly for edit can
            // silently act on the wrong booking. Drill into day view instead, matching what
            // clicking blank month-view space already does.
            setCalendarType(CalendarType.DAY)
            changeDate(target.date)
            break
          case 'booking':
            setSidebar({ open: true, type: SidebarType.BOOKING_EDIT, booking: target.booking })
            break
          case 'foodEvent':
            const event = foodEvents.find((e) => e.id === argument.event.id)
            if (event) {
              setSidebar({
                open: true,
                type: SidebarType.EVENT_EDIT,
                event,
              })
            }
            break
        }
        break
    }
  }

  // Marks event pills for recurring bookings with a small repeat icon. Month view is excluded
  // since its cells aggregate multiple bookings and only carry one representative booking's data
  // (see handleOnEventClick), so a badge there wouldn't reliably reflect the whole cell.
  const handleEventDidMount = (info: EventMountArg) => {
    if (calendarType === CalendarType.MONTH) return

    const data = events?.find((event) => event.id === info.event.id) as CalendarEvent | undefined
    if (isBadgelessEventType(data?.type)) return
    if (!data || getEntityType(data) !== 'booking') return

    const booking = getBooking(data)
    if (!booking?.seriesId) return

    const titleEl = info.el.querySelector('.fc-event-title') ?? info.el
    const badge = document.createElement('span')
    badge.title = 'Teil einer wiederkehrenden Serie'
    badge.style.display = 'inline-flex'
    badge.style.verticalAlign = 'middle'
    badge.style.marginRight = '4px'
    badge.innerHTML = renderToStaticMarkup(<Repeat className="h-3 w-3" />)
    titleEl.prepend(badge)
  }

  const resourceLabelContent = useMemo(() => {
    // sortable wrapper component for each room label
    const SortableResource: React.FC<{ id: string; children: React.ReactNode }> = ({
      id,
      children,
    }) => {
      const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
      })
      const style: React.CSSProperties = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        opacity: isDragging ? 0.5 : 1,
        // put dragged element on top of all calendar rows
        position: 'relative',
        zIndex: isDragging ? 10000 : undefined,
      }
      return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
          {children}
        </div>
      )
    }

    return (arg: ResourceLabelContentArg) => {
      const isRoom = !arg.resource._resource.parentId

      const content = (
        <div className="group flex w-full items-center justify-between gap-2">
          <span>{arg.resource.title}</span>
          {isRoom && (
            <GripVertical className="h-4 w-4 cursor-move opacity-0 group-hover:opacity-100" />
          )}
        </div>
      )

      if (isRoom) {
        return <SortableResource id={arg.resource.id}>{content}</SortableResource>
      }
      return content
    }
  }, [tenant, events, orderedResources, moveResourceUp, moveResourceDown])

  useLayoutEffect(() => {
    if (
      calendarRef.current &&
      initialViewCalendar &&
      initialViewCalendar !== previousViewRef.current
    ) {
      const api = calendarRef.current.getApi()
      if (api && api.view) {
        api.changeView(initialViewCalendar)
        previousViewRef.current = initialViewCalendar

        // Scroll to current time for time-based views (day/week) if nowIndicator is available
        if (
          (calendarType === CalendarType.DAY || calendarType === CalendarType.WEEK) &&
          tenant?.timezone
        ) {
          // Use setTimeout to ensure the view has been rendered before scrolling
          setTimeout(() => {
            if (calendarRef.current) {
              const currentApi = calendarRef.current.getApi()
              if (currentApi && currentApi.view) {
                // Scroll to current time in the tenant's timezone
                const now = new Date()
                const currentTimeInTenantTz = formatInTimeZone(now, tenant.timezone, 'HH:mm')
                api.scrollToTime(currentTimeInTenantTz)
              }
            }
          }, 100)
        }
      }
    }
  }, [initialViewCalendar, calendarType, tenant?.timezone])

  // Scroll to current time when calendar is first loaded
  useLayoutEffect(() => {
    if (
      calendarRef.current &&
      (calendarType === CalendarType.DAY || calendarType === CalendarType.WEEK) &&
      tenant?.timezone &&
      !eventsLoading &&
      !resourcesLoading
    ) {
      // Use setTimeout to ensure everything is loaded before scrolling
      setTimeout(() => {
        if (calendarRef.current) {
          const api = calendarRef.current.getApi()
          if (api && api.view) {
            const now = new Date()
            const currentTimeInTenantTz = formatInTimeZone(now, tenant.timezone, 'HH:mm')
            api.scrollToTime(currentTimeInTenantTz)
          }
        }
      }, 500)
    }
  }, [calendarType, tenant?.timezone, eventsLoading, resourcesLoading])

  const ResourceDragOverlay: React.FC<{ id: string }> = ({ id }) => {
    const res = orderedResources.find((r) => r.id === id)
    if (!res) return null
    const isRoom = !res.parentId
    return (
      <div className="flex w-full items-center justify-between gap-2 bg-white p-1 shadow-lg">
        <span className="whitespace-nowrap">{res.title}</span>
        {isRoom && <GripVertical className="h-4 w-4" />}
      </div>
    )
  }

  return (
    <Loading loading={tenantLoading || eventsLoading || resourcesLoading} label="Kalender lädt...">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setActiveId(e.active.id as string)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <SortableContext items={roomsOnly.map((r) => r.id)}>
          <DragOverlay>{activeId ? <ResourceDragOverlay id={activeId} /> : null}</DragOverlay>
          <div
            className={`${calendarType.toLowerCase()} h-full w-full`}
            data-divider-pos={dividerPosition ?? undefined}
          >
            <FullCalendar
              events={events || []}
              resources={orderedResources}
              resourceOrder={tenant?.calendarRoomOrder ?? 'order'}
              ref={calendarRef}
              schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
              resourceAreaHeaderContent="Bereich"
              resourceLabelContent={resourceLabelContent}
              initialView={initialViewCalendar}
              plugins={plugins}
              initialDate={selectedDate}
              locale={deLocale}
              timeZone={tenant?.timezone}
              nowIndicator
              headerToolbar={false}
              droppable
              editable
              selectable
              eventOverlap
              selectOverlap={(event) => event.display === 'background'}
              showNonCurrentDates={false}
              resourceAreaWidth={resourceAreaWidth}
              slotDuration={formatSlotDuration(slotDurationPrefs)}
              slotLabelInterval={{ minutes: labelInterval }}
              slotLabelContent={(arg) => {
                const { date } = arg
                if (!date) {
                  return null
                }

                const hour = date.getHours()
                const minute = date.getMinutes()
                const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

                let totalPeople = 0
                let totalTables = 0
                if (tenant?.type === TenantType.FOOD && showUtilization) {
                  const bookings = events
                    .map((e) => getBooking(e))
                    .filter(Boolean) as FoodBookingModel[]
                  const result = calculateTableUtilizationForTime(
                    bookings,
                    tenant,
                    date,
                    labelInterval
                  )
                  if (result && result.totalPeople > 0) {
                    totalPeople = result.totalPeople
                    totalTables = result.totalTables
                  }
                }

                return (
                  <div
                    className={cn(
                      'inline-flex flex-col items-start whitespace-normal p-1',
                      totalPeople > 0 && 'font-semibold'
                    )}
                  >
                    <span
                      className={cn({
                        'font-bold': totalPeople > 0,
                      })}
                    >
                      {timeString}
                    </span>
                    {totalPeople > 0 && (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-normal">
                        <div className="flex items-center">
                          <UsersIcon className="mr-1 h-[12px] w-[12px]" />
                          {totalPeople}
                        </div>
                        |
                        <div className="flex items-center">
                          <TableIcon className="mr-1 h-[12px] w-[12px]" />
                          {totalTables}
                        </div>
                      </span>
                    )}
                  </div>
                )
              }}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }}
              resourceLaneClassNames={(props) => {
                const resourceIndex = orderedResources.findIndex((r) => r.id === props.resource.id)
                return cn(resourceIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white')
              }}
              resourceLabelClassNames={(props) => {
                const resourceIndex = orderedResources.findIndex((r) => r.id === props.resource.id)
                return cn(resourceIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white')
              }}
              select={(argument) => selectDateSlot(argument)}
              eventDrop={(argument) => moveBooking(argument)}
              eventClick={(argument) => handleOnEventClick(argument)}
              eventDidMount={(info) => handleEventDidMount(info)}
              height={fullscreen ? 'calc(100vh - 120px)' : '85vh'}
              {...(tenant?.calendarTimeRange && {
                slotMinTime: tenant.calendarTimeRange.minTime,
                slotMaxTime: tenant.calendarTimeRange.maxTime,
              })}
            />
          </div>
        </SortableContext>
      </DndContext>
    </Loading>
  )
}

export default CalendarView
